import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Building2,
  MessageSquare,
  Search,
  Send,
  ShieldAlert,
  Users,
  X,
} from 'lucide-react';
import { formatIstTime, relativeTime } from '@gbv/utils';
import { Badge, Button, Spinner, Textarea, cn, useToast } from '@gbv/ui';
import { api, ApiRequestError } from '../lib/api';

/**
 * The admin's floating chat: every conversation on the platform, in one place.
 *
 * Employees see the clients they are assigned to. An admin sees all of them,
 * both channels each — which makes this the one screen where someone is
 * routinely reading an internal channel about a client they have never spoken
 * to, then switching to a thread that client can read, in the same panel.
 *
 * That adjacency is the whole risk. Two defences, both deliberate:
 *
 *   1. The banner above the composer restates who reads the channel, and the
 *      client channel is tinted warm so the two never look alike at a glance.
 *   2. The composer placeholder names the audience too, because a banner above
 *      the message list is not where the eye is when typing.
 *
 * Reads are audited server-side. Opening someone's private conversation is a
 * real act, and it leaves a record.
 */

interface ConversationSummary {
  orgId: string;
  orgName: string;
  orgCode: string;
  logoUrl: string | null;
  lastActivityAt: string | null;
  client: {
    lastBody: string | null;
    lastSenderName: string | null;
    lastAt: string | null;
    awaitingReply: number;
    total: number;
  };
  team: {
    lastBody: string | null;
    lastSenderName: string | null;
    lastAt: string | null;
    total: number;
  };
}

interface ClientMessage {
  id: string;
  body: string;
  senderType: 'CLIENT' | 'EMPLOYEE';
  senderName: string;
  createdAt: string;
}

interface TeamMessage {
  id: string;
  body: string;
  senderId: string;
  senderName: string;
  isMine: boolean;
  replyToBody: string | null;
  replyToSender: string | null;
  editedAt: string | null;
  createdAt: string;
}

type Channel = 'client' | 'team';

export function FloatingChat() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState<ConversationSummary | null>(null);
  const [channel, setChannel] = useState<Channel>('client');
  const [draft, setDraft] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  const conversations = useQuery({
    queryKey: ['admin-chats', search],
    queryFn: () =>
      api.get<{ data: ConversationSummary[] }>(
        `/chats${search.trim() ? `?search=${encodeURIComponent(search.trim())}` : ''}`,
        { skipOrg: true },
      ),
    // Polled while shut so the badge is right without the panel being open.
    refetchInterval: open ? 30_000 : 90_000,
  });

  /**
   * Threads are fetched on open and after sending, never on a timer. Every read
   * writes an audit row, and a poll would turn that trail into thousands of
   * identical "viewed" entries with the real ones buried among them.
   */
  const thread = useQuery({
    queryKey: ['admin-chat-thread', active?.orgId, channel],
    queryFn: () =>
      api.get<{ messages: (ClientMessage | TeamMessage)[] }>(
        `/chats/${active!.orgId}/${channel}`,
        { skipOrg: true },
      ),
    enabled: Boolean(active) && open,
  });

  useEffect(() => {
    if (!open || !active) return;
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [open, active, channel, thread.data]);

  // Opening a client thread clears the "awaiting reply" badge for everyone.
  useEffect(() => {
    if (!open || !active || channel !== 'client') return;
    void api.post(`/chats/${active.orgId}/client/read`, undefined, { skipOrg: true }).then(() => {
      void queryClient.invalidateQueries({ queryKey: ['admin-chats'] });
    });
  }, [open, active, channel, queryClient]);

  const send = useMutation({
    mutationFn: () =>
      api.post(`/chats/${active!.orgId}/${channel}`, { body: draft.trim() }, { skipOrg: true }),
    onSuccess: () => {
      setDraft('');
      void queryClient.invalidateQueries({ queryKey: ['admin-chat-thread'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-chats'] });
    },
    onError: (err) =>
      toast.error(
        'Message not sent',
        err instanceof ApiRequestError ? err.message : 'Try again in a moment.',
      ),
  });

  const rows = conversations.data?.data ?? [];
  const awaitingTotal = useMemo(
    () => rows.reduce((sum, r) => sum + r.client.awaitingReply, 0),
    [rows],
  );

  const messages = thread.data?.messages ?? [];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'fixed bottom-5 right-5 z-40 flex h-12 w-12 items-center justify-center rounded-full shadow-overlay transition',
          open ? 'bg-ink text-white' : 'bg-vir text-white hover:bg-vir-dark',
        )}
        aria-label={open ? 'Close chat' : 'Open chat'}
      >
        {open ? <X size={19} /> : <MessageSquare size={19} />}
        {!open && awaitingTotal > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-white">
            {awaitingTotal > 99 ? '99+' : awaitingTotal}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed bottom-20 right-5 z-40 flex h-[560px] max-h-[calc(100vh-7rem)] w-[400px] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-card border border-line bg-white shadow-overlay">
          {active ? (
            <>
              <div className="flex items-center gap-2 border-b border-line px-3 py-2">
                <button
                  type="button"
                  onClick={() => {
                    setActive(null);
                    setDraft('');
                  }}
                  aria-label="Back to all conversations"
                  className="shrink-0 rounded p-1 text-ink-muted hover:bg-surface-sunken hover:text-ink"
                >
                  <ArrowLeft size={15} />
                </button>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-semibold text-ink">{active.orgName}</p>
                  <p className="truncate font-mono text-[9px] uppercase tracking-wider text-ink-soft">
                    {active.orgCode}
                  </p>
                </div>
              </div>

              <div className="flex gap-1 border-b border-line px-3">
                <ChannelTab
                  active={channel === 'client'}
                  onClick={() => {
                    setChannel('client');
                    setDraft('');
                  }}
                  icon={Building2}
                  label="Client"
                  count={active.client.awaitingReply}
                />
                <ChannelTab
                  active={channel === 'team'}
                  onClick={() => {
                    setChannel('team');
                    setDraft('');
                  }}
                  icon={Users}
                  label="Team"
                />
              </div>

              {/* Restated on every channel switch, because the panel above it
                  looks identical either way. */}
              <p
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 text-[10px]',
                  channel === 'client'
                    ? 'bg-bee-light font-semibold text-ink'
                    : 'bg-surface-page text-ink-muted',
                )}
              >
                {channel === 'client' ? <ShieldAlert size={11} /> : <Users size={11} />}
                {channel === 'client'
                  ? `${active.orgName} reads this thread and can reply.`
                  : 'Internal — staff only. The client cannot see this channel.'}
              </p>

              <div className="flex-1 space-y-2 overflow-y-auto px-3 py-3">
                {thread.isLoading ? (
                  <div className="flex h-full items-center justify-center">
                    <Spinner />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center px-6 text-center">
                    <MessageSquare size={20} className="text-ink-soft" />
                    <p className="mt-2 text-sm text-ink">
                      {channel === 'client' ? 'Nobody has messaged them yet' : 'No internal notes yet'}
                    </p>
                  </div>
                ) : (
                  messages.map((m) => {
                    const team = channel === 'team' ? (m as TeamMessage) : null;
                    const mine =
                      channel === 'team'
                        ? (m as TeamMessage).isMine
                        : (m as ClientMessage).senderType === 'EMPLOYEE';

                    return (
                      <div key={m.id} className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
                        <div
                          className={cn(
                            'max-w-[85%] rounded-card px-3 py-2',
                            mine ? 'bg-vir text-white' : 'bg-surface-page text-ink',
                          )}
                        >
                          <p
                            className={cn(
                              'mb-0.5 text-[10px] font-semibold',
                              mine ? 'text-white/70' : 'text-ink-soft',
                            )}
                          >
                            {m.senderName}
                          </p>

                          {team?.replyToBody && (
                            <p
                              className={cn(
                                'mb-1 truncate border-l-2 pl-1.5 text-[10px]',
                                mine ? 'border-white/40 text-white/70' : 'border-line text-ink-soft',
                              )}
                            >
                              {team.replyToSender}: {team.replyToBody}
                            </p>
                          )}

                          <p className="whitespace-pre-wrap break-words text-sm">{m.body}</p>
                          <p
                            className={cn(
                              'mt-0.5 text-[9px]',
                              mine ? 'text-white/60' : 'text-ink-soft',
                            )}
                          >
                            {formatIstTime(new Date(m.createdAt))}
                            {team?.editedAt && ' · edited'}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={bottomRef} />
              </div>

              <div className="border-t border-line p-2">
                <Textarea
                  rows={2}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (draft.trim()) send.mutate();
                    }
                  }}
                  placeholder={
                    channel === 'client'
                      ? `Message ${active.orgName} — they will see this…`
                      : 'Internal note to the team on this client…'
                  }
                  aria-label={channel === 'client' ? 'Message the client' : 'Message the team'}
                />
                <div className="mt-1.5 flex items-center justify-between gap-2">
                  <span className="text-[10px] text-ink-soft">Sends as you · Enter to send</span>
                  <Button
                    size="sm"
                    leftIcon={<Send size={12} />}
                    onClick={() => send.mutate()}
                    isLoading={send.isPending}
                    disabled={draft.trim().length === 0}
                  >
                    Send
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="border-b border-line px-3 py-2.5">
                <p className="text-[13px] font-semibold text-ink">All conversations</p>
                <p className="text-[11px] text-ink-muted">
                  Every client, both channels. Opening one is recorded.
                </p>
              </div>

              <div className="border-b border-line px-3 py-2">
                <div className="flex items-center gap-2 rounded-control border border-line px-2 py-1.5">
                  <Search size={13} className="shrink-0 text-ink-soft" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search clients…"
                    aria-label="Search clients"
                    className="min-w-0 flex-1 bg-transparent text-[12.5px] text-ink outline-none placeholder:text-ink-soft"
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {conversations.isLoading ? (
                  <div className="flex h-full items-center justify-center">
                    <Spinner />
                  </div>
                ) : rows.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center px-6 text-center">
                    <Search size={20} className="text-ink-soft" />
                    <p className="mt-2 text-sm text-ink">No client matches that</p>
                  </div>
                ) : (
                  rows.map((row) => (
                    <button
                      key={row.orgId}
                      type="button"
                      onClick={() => {
                        setActive(row);
                        // Always land on the client channel. Opening straight
                        // into an internal thread would make the safer of the
                        // two channels the one you have to go looking for.
                        setChannel('client');
                        setDraft('');
                      }}
                      className="flex w-full flex-col gap-1 border-b border-line px-3 py-2.5 text-left transition-colors hover:bg-surface-page"
                    >
                      <div className="flex items-center gap-2">
                        <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-ink">
                          {row.orgName}
                        </span>
                        {row.client.awaitingReply > 0 && (
                          <Badge tone="danger">{row.client.awaitingReply}</Badge>
                        )}
                        <span className="shrink-0 font-mono text-[9px] text-ink-soft">
                          {row.lastActivityAt ? relativeTime(new Date(row.lastActivityAt)) : '—'}
                        </span>
                      </div>

                      <ChannelPreview
                        icon={Building2}
                        label="Client"
                        sender={row.client.lastSenderName}
                        body={row.client.lastBody}
                        total={row.client.total}
                      />
                      <ChannelPreview
                        icon={Users}
                        label="Team"
                        sender={row.team.lastSenderName}
                        body={row.team.lastBody}
                        total={row.team.total}
                        internal
                      />
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

function ChannelTab({
  active,
  onClick,
  icon: Icon,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Users;
  label: string;
  count?: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-1 items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold transition',
        active
          ? 'border-b-2 border-vir text-ink'
          : 'border-b-2 border-transparent text-ink-muted hover:text-ink',
      )}
    >
      <Icon size={13} />
      {label}
      {count !== undefined && count > 0 && <Badge tone="danger">{count > 9 ? '9+' : count}</Badge>}
    </button>
  );
}

/** One line per channel in the list, so a silent channel is visibly silent. */
function ChannelPreview({
  icon: Icon,
  label,
  sender,
  body,
  total,
  internal,
}: {
  icon: typeof Users;
  label: string;
  sender: string | null;
  body: string | null;
  total: number;
  internal?: boolean;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon size={10} className={cn('shrink-0', internal ? 'text-ink-soft' : 'text-vir')} />
      <span
        className={cn(
          'shrink-0 font-mono text-[8px] uppercase tracking-wider',
          internal ? 'text-ink-soft' : 'text-vir',
        )}
      >
        {label}
      </span>
      <span className="min-w-0 flex-1 truncate text-[11px] text-ink-muted">
        {body ? (
          <>
            {sender && <span className="text-ink-soft">{sender}: </span>}
            {body}
          </>
        ) : (
          <span className="text-ink-soft">
            {total === 0 ? 'Nothing yet' : `${total} messages`}
          </span>
        )}
      </span>
    </div>
  );
}
